// 사람 이름 표기 규칙 — 호스트에서 이름을 보여주는 곳은 모두 이 함수를 거친다.
//
//   한국어 이름  성이름   (붙여 씀)   김민준
//   일본어 이름  성 이름  (띄어 씀)   田中 太郎
//   영어 이름    이름 성  (띄어 씀)   John Smith
//
// 이름이 어느 언어인지는 표시 언어(UI)가 아니라 이름 글자(문자 체계)로 판정한다 — 일본어 화면에서도
// 한국인 이름은 '김민준' 으로 보여야 하기 때문이다. 한글이 있으면 ko, 가나·한자가 있으면 ja,
// 그 밖(라틴 등)은 en. 판정할 글자가 없으면 호출자가 준 폴백 언어(보통 현재 표시 언어)를 쓴다.
import type { LangId } from './locale';

export interface PersonName {
  /** 성 (Keycloak family_name) */
  familyName?: string | null;
  /** 이름 (Keycloak given_name) */
  givenName?: string | null;
  /** 이미 합쳐진 표시용 이름 — 성/이름을 못 받았을 때의 폴백 (Keycloak name) */
  fullName?: string | null;
}

const HANGUL = /[ᄀ-ᇿ㄰-㆏가-힯]/;
const JAPANESE = /[぀-ヿㇰ-ㇿ一-鿿ｦ-ﾟ]/;

/** 이름 글자로 언어 판정 — 모르면 null */
export function detectNameLang(...parts: Array<string | null | undefined>): LangId | null {
  const text = parts.filter(Boolean).join('');
  if (!text) return null;
  if (HANGUL.test(text)) return 'ko';
  if (JAPANESE.test(text)) return 'ja';
  return /[A-Za-z]/.test(text) ? 'en' : null;
}

/** 성·이름을 규칙대로 합친다 */
export function formatPersonName(name: PersonName, fallbackLang: LangId = 'ko'): string {
  const family = name.familyName?.trim() ?? '';
  const given = name.givenName?.trim() ?? '';
  const full = name.fullName?.trim() ?? '';

  // 성·이름을 따로 못 받았으면 합쳐진 이름을 그대로 쓴다 — 어디서 나눌지 알 수 없으므로 추측하지 않는다
  if (!family || !given) return family || given || full;

  const lang = detectNameLang(family, given) ?? fallbackLang;
  if (lang === 'ko') return `${family}${given}`;
  if (lang === 'ja') return `${family} ${given}`;
  return `${given} ${family}`;
}
